import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getCommonDetails } from "redux/actions";
import { moveCartToNewStore, selectCurrentStore } from "redux/actions/store";
import { getLocalStorageValue, setLocalStorageValue } from 'config/helper';
import { useApiDefaults } from 'hooks/useApiDefaults';
import SearchIcon from "assets/icons/search.svg";
import LocationIcon from "assets/icons/location.svg";
import "./store-location-popup-theme2.scss";
import useSmoothScroll from 'hooks/useSmoothScroll';
import { DeliveryIcon, ListBuilderIcon, PickupIcon } from 'components/Icons';
import IconBadge from "./IconBadge";
import CateringIcon from 'components/Icons/CateringIcon';
import MealkitIcon from 'components/Icons/MealkitIcon';
import { getNearestStoreByZip } from 'config/nearestZipCode';

const StoreTypes = {
  ALL_STORES: "all_stores",
  PICKUP: "pickup",
  DELIVERY: "delivery",
}

function isPickUpStore(store) {
  return Boolean(store.allow_pickup === "1" || 
                 store.allow_mealkit_pickup === "1" || 
                 store.catering_pickup_cc === "1"
              )
}

function isDeliveryStore(store) {
  return Boolean(store.allow_delivery === "1");
}

const StoreLocationModalTheme2 = (props) => {
  const location = useLocation();
  const { isShowDeliveryPopup, onClose, selectedStore } = props;
  const [storeType, setStoreType] = useState(StoreTypes.ALL_STORES);

  const oldStoreRef = useRef(null);
  const searchTextRef = useRef(null);
  const storeListContainerRef = useRef(null);
  const [currentStore, setCurrentStore] = useState();

  const apiDefaults = useApiDefaults();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const scrollToElement = useSmoothScroll();

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const clientId = getLocalStorageValue("RSAclient-id");
  const [searchValue, setSearchValue] = useState("");
  const [filteredStoreList, setFilteredStoreList] = useState([]);
  const [isLocationPermissionGranted, setIsLocationPermissionGranted] = useState(false);

  const storeList = useSelector(({ store }) => store.storeListData);
  async function getData(storeList, searchValue, storeType, isLocationPermissionGranted) {
    const _storelist = await getFilteredStores(storeList, searchValue, storeType, isLocationPermissionGranted)
    setFilteredStoreList(_storelist)
  }
  useEffect(() => {
    getData(storeList, searchValue, storeType, isLocationPermissionGranted)
  }, [storeList, searchValue, storeType, isLocationPermissionGranted]);

  const pickUpStoresAvailable = storeList?.filter(isPickUpStore)?.length > 0;
  const deliveryStoresAvailable = storeList?.filter(isDeliveryStore)?.length > 0;
  const hideTopFilters = !pickUpStoresAvailable && !deliveryStoresAvailable;

  const isUserLoggedIn = useSelector(({ user }) => user.isUserLoggedIn)

  const headerSectionData = useSelector(
    ({ common }) => common.commonDetailsData.header_section
  );

  const {
    productCartList: groceryProductCartList,
    cateringAndMealKitProductCartList,
  } = useSelector(({ cartWishlist }) => cartWishlist);

  const itemsAvailableInCart = (groceryProductCartList?.length || 0) + (cateringAndMealKitProductCartList?.length || 0) > 0;
  const isConfirmationRequired = isShowDeliveryPopup && isUserLoggedIn && itemsAvailableInCart;

  useEffect(() => {
    oldStoreRef.current = selectedStore;
    setCurrentStore(selectedStore);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStore])

  useEffect(() => {
    // scroll to the current store card after the modal is opened
    if (isShowDeliveryPopup && !showConfirmationModal && filteredStoreList?.length) {
      searchTextRef.current?.focus();
      const currentStoreId = selectedStore?.ClientStoreId;
      if (currentStoreId) {
        let retryCount = 0;
        const maxRetries = 10;
        const scrollToTarget = () => {
          // Use ref instead of getElementById to avoid duplicate ID issues
          const containerElement = storeListContainerRef.current;
          if (containerElement) {
            const targetElement = containerElement.querySelector(`#store-item-${currentStoreId}`);
            if (targetElement) {
              // Get the offset of the first item to account for container padding/wrapper offset
              const firstItem = containerElement.querySelector('.store-item');
              const baseOffset = firstItem ? firstItem.offsetTop : 0;
              containerElement.scrollTop = targetElement.offsetTop - baseOffset - 1;
            } else if (retryCount < maxRetries) {
              retryCount++;
              requestAnimationFrame(() => {
                requestAnimationFrame(scrollToTarget);
              });
            }
          } else if (retryCount < maxRetries) {
            retryCount++;
            requestAnimationFrame(() => {
              requestAnimationFrame(scrollToTarget);
            });
          }
        };
        requestAnimationFrame(() => {
          requestAnimationFrame(scrollToTarget);
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowDeliveryPopup, showConfirmationModal, filteredStoreList?.length, selectedStore?.ClientStoreId])

  useEffect(() => {
    if (isConfirmationRequired) {
      setShowConfirmationModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfirmationRequired])

  useEffect(() => {
    if (!isShowDeliveryPopup) {
      setCurrentStore(selectedStore);
    }
  }, [isShowDeliveryPopup, selectedStore])

  const [isLocationPermissionDenied, setIsLocationPermissionDenied] = useState(false);

  useEffect(() => {
    if (navigator?.permissions?.query) {
      navigator?.permissions?.query({ name: 'geolocation' }).then((result) => {
        if (result.state === 'granted') {
          setIsLocationPermissionGranted(true);
          setIsLocationPermissionDenied(false);
        } else if (result.state === 'denied') {
          setIsLocationPermissionDenied(true);
        }
        result.onchange = () => {
          if (result.state === 'granted') {
            setIsLocationPermissionGranted(true);
            setIsLocationPermissionDenied(false);
          } else if (result.state === 'denied') {
            setIsLocationPermissionGranted(false);
            setIsLocationPermissionDenied(true);
          } else {
             setIsLocationPermissionGranted(false);
             setIsLocationPermissionDenied(false);
          }
        };
      });
    } else {
    }
  }, []);

  const handleLocationClick = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setIsLocationPermissionGranted(true);
        },
        (error) => {
          console.error('Location permission needed to show nearby stores', error);
        }
      );
    }
  };

  const handleCloseModal = () => {
    setSearchValue("");
    dispatch(selectCurrentStore(currentStore));
    setLocalStorageValue('selected-store-id', currentStore.ClientStoreId);
    setLocalStorageValue("selected-initial-store-id", currentStore.ClientStoreId)

    const aogEndPointURL = getLocalStorageValue("api-end-point-aog");
    if (apiDefaults.RSAClientId && aogEndPointURL) {
      const body = {
        ...apiDefaults,
        ClientStoreId: parseInt(currentStore.ClientStoreId),
        RSAClientId: clientId,
      };
      dispatch(getCommonDetails(body));

      const oldStoreId = +oldStoreRef.current?.ClientStoreId
      const newStoreId = +currentStore.ClientStoreId

      if (isUserLoggedIn && oldStoreId !== newStoreId) {
        moveCartToNewStore({ ...body, "FromClientStoreId": oldStoreId })
      }
    }

    if (location.pathname.startsWith("/weekly-ads/")) {
      const clientStoreId = currentStore.ClientStoreId;
      const clientStoreName = encodeURIComponent(currentStore.ClientStoreName);
      const url = `/weekly-ads/${clientStoreId}/${clientStoreName}`;
      navigate(url);
    }

    onClose();
  }

  function handleCancelClick() {
    setShowConfirmationModal(false)
    onClose()
  }

  if (showConfirmationModal) {
    return (
      <div className="modal-backdrop">
        <div className="modal-content">
          <button className="close-btn" onClick={handleCancelClick}>&times;</button>
          <div className="message">
            Changing the store will update your cart with items available at the
            new location. Product availability and pricing may vary by store.
          </div>
          <div className="message">Do you want to continue?</div>
          <div className="modal-actions">
            <button
              className="continue-btn"
              onClick={() => {
                setShowConfirmationModal(false)
                setConfirm(true)
              }}
            >
              Continue
            </button>
            <button
              className="cancel-btn"
              onClick={handleCancelClick}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (isConfirmationRequired && !confirm) {
    return null
  }

  let title = "Select Your Store"
  let searchPlaceholder = "Search by street address, city, or zip code"
  if (+clientId === 347) {
    title = "Select Recipient"
    searchPlaceholder = ""
  }

  return (
    <>
      {isShowDeliveryPopup && (
        <div className="store-main-wrap-theme2">
          <div className="store-selector-modal">
            <div className="modal-content2">
              <div className="top-header-wrap">
              <h2>{title}</h2>
              </div>
              <button className="close" onClick={() => {
                setSearchValue("");
                onClose()
              }}>
                &times;
              </button>
              {hideTopFilters ? null : <div className="pikup-wrap">
                <button className={storeType === StoreTypes.ALL_STORES ? "active" : ""} onClick={() => setStoreType(StoreTypes.ALL_STORES)}>All Stores</button>
                {pickUpStoresAvailable ? <button className={storeType === StoreTypes.PICKUP ? "active" : ""} onClick={() => setStoreType(StoreTypes.PICKUP)}>
                  <span className="filter-icon" aria-hidden="true">
                    <PickupIcon />
                  </span>
                  Pickup
                </button> : null}
                {deliveryStoresAvailable ? <button className={storeType === StoreTypes.DELIVERY ? "active" : ""} onClick={() => setStoreType(StoreTypes.DELIVERY)}>
                  <span className="filter-icon" aria-hidden="true">
                    <DeliveryIcon />
                    </span>
                    Delivery
                  </button> : null}
              </div>}
              <span className="search-text">{searchPlaceholder}</span>
              <div className={`location-search${searchPlaceholder && !isLocationPermissionGranted && !isLocationPermissionDenied ? ' with-nearby-btn' : ''}`}>
              {searchPlaceholder && !isLocationPermissionGranted && !isLocationPermissionDenied ? (
                  <button 
                    className="show-nearby-btn" 
                    onClick={handleLocationClick}
                    type="button"
                  >
                    <img src={LocationIcon} alt="" />
                    Show Nearby
                  </button>
                ) : (
                  <img
                    src={SearchIcon}
                    className="search-img"
                    alt="SearchIcon"
                  />
                )}
                <input type="text" value={searchValue} onChange={e => setSearchValue(e.target.value)} ref={searchTextRef} />
              </div>

              <div className="store-details">
                <div className="store-location-wrap" id="store-list-container" ref={storeListContainerRef}>
                  <div className="store-info-wrapper">
                    <div className="store-info">
                      {filteredStoreList?.length ? (
                        filteredStoreList.map(store => {
                          const isSelected = store.ClientStoreId === currentStore?.ClientStoreId;
                          
                          const pickupIcon = Boolean(store.allow_pickup === "1" || store.allow_mealkit_pickup === "1" || store.catering_pickup_cc === "1")
                          const deliveryIcon = Boolean(store.allow_delivery === "1")
                          const ecomIcon = Boolean(store.allow_ecommerce === "1")
                          const cateringIcon = Boolean(store.allow_catering_web === "1")
                          const mealkitIcon = Boolean(store.mealkit_web === "1")

                          const showIcons = pickupIcon || deliveryIcon || ecomIcon || cateringIcon || mealkitIcon

                          return (
                            <div
                              key={store.ClientStoreId}
                              id={`store-item-${store.ClientStoreId}`}
                              className={`store-item${isSelected ? " selected" : ""}`}
                              onClick={() => setCurrentStore(store)}
                              onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  setCurrentStore(store);
                                }
                              }}
                              role="button"
                              tabIndex={0}
                              aria-pressed={isSelected}
                            >
                            <div className="store-logo-wrap">
                              <img src={headerSectionData.rsa_client_logo} alt={headerSectionData.image_alt_text} className="store-logo" />
                            </div>
                            <div className="store-detail">

                              <div className="store-name-row">
                                <h3>{store.ClientStoreName}</h3>
                                {store.miles ? (
                                  <span className="distance-badge">{store.miles.toFixed(2)} miles</span>
                                ) : null}
                              </div>
                              <p>{store.AddressLine1}, {store.City}, {store.StateName} - {store.ZipCode}</p>
                              <p className="timing"><span>Open •</span>  {store.StoreTimings}</p>
                              <p><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 512 512"><path fill="currentColor" d="M478.94 370.14c-5.22-5.56-23.65-22-57.53-43.75c-34.13-21.94-59.3-35.62-66.52-38.81a3.83 3.83 0 0 0-3.92.49c-11.63 9.07-31.21 25.73-32.26 26.63c-6.78 5.81-6.78 5.81-12.33 4c-9.76-3.2-40.08-19.3-66.5-45.78s-43.35-57.55-46.55-67.3c-1.83-5.56-1.83-5.56 4-12.34c.9-1.05 17.57-20.63 26.64-32.25a3.83 3.83 0 0 0 .49-3.92c-3.19-7.23-16.87-32.39-38.81-66.52c-21.78-33.87-38.2-52.3-43.76-57.52a3.9 3.9 0 0 0-3.89-.87a322.4 322.4 0 0 0-56 25.45A338 338 0 0 0 33.35 92a3.83 3.83 0 0 0-1.26 3.74c2.09 9.74 12.08 50.4 43.08 106.72c31.63 57.48 53.55 86.93 100 133.22S252 405.21 309.54 436.84c56.32 31 97 41 106.72 43.07a3.86 3.86 0 0 0 3.75-1.26A337.7 337.7 0 0 0 454.35 430a322.7 322.7 0 0 0 25.45-56a3.9 3.9 0 0 0-.86-3.86" /></svg>&nbsp;{store.StorePhoneNumber}</p>
                              {showIcons ? (
                                  <div className="icons" aria-label="Available services">
                                    {pickupIcon ? (
                                      <IconBadge Icon={PickupIcon} tooltip="Pickup" />
                                    ) : null}
                                    {deliveryIcon ? (
                                      <IconBadge Icon={DeliveryIcon} tooltip="Delivery" />
                                    ) : null}
                                    {ecomIcon ? (
                                      <IconBadge Icon={ListBuilderIcon} tooltip="Shopping List" />
                                    ) : null}
                                    {cateringIcon ? (
                                      <IconBadge Icon={CateringIcon} tooltip="Catering" />
                                    ) : null}
                                    {mealkitIcon ? (
                                      <IconBadge Icon={MealkitIcon} tooltip="Made to Order" />
                                    ) : null}
                                  </div>
                                ) : null}
                            </div>
                          </div>
                          );
                        })
                      ) : (
                        <p>No stores available</p>
                      )}
                    </div>


                  </div>
                </div>
                <div className="modal-actions2">
                  <button className="continue-button" onClick={handleCloseModal}>
                    {+clientId === 347 ? "Continue" : "Start Shopping"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

async function getFilteredStores(storeList, searchValue, storeType, isLocationPermissionGranted) {
  const normalizedSearchValue = searchValue?.trim()?.toLowerCase() ?? ""
  
  let filteredStoreList = storeList?.filter(s => {
    if (storeType === StoreTypes.ALL_STORES) {
      return true;
    } else if (storeType === StoreTypes.PICKUP) {
      return isPickUpStore(s);
    } else if (storeType === StoreTypes.DELIVERY) {
      return isDeliveryStore(s);
    }
    return false;
  })
  
  const zipCodeBasedNearBySearch = normalizedSearchValue?.length === 5 && +normalizedSearchValue > 0
  const userLocationBasedNearBySearch = !normalizedSearchValue && isLocationPermissionGranted
  if (zipCodeBasedNearBySearch || userLocationBasedNearBySearch) {
    return await getNearestStoreByZip(filteredStoreList, normalizedSearchValue, isLocationPermissionGranted);
  }

  if (!normalizedSearchValue && !isLocationPermissionGranted) {
    return filteredStoreList;
  }

  const {startsWithMatches, includesMatches} = (filteredStoreList || []).reduce(
    (acc, store) => {
      const values = [
        store.ClientStoreName,
        store.AddressLine1,
        store.AddressLine2,
        store.City,
        store.StateName,
        store.ZipCode,
      ].map(value => value?.toLowerCase() || "")
      const startsWithMatch = values.some(value =>
        value.startsWith(normalizedSearchValue)
      )
      if (startsWithMatch) {
        acc.startsWithMatches.push(store)
        return acc
      }
      if (values.some(value => value.includes(normalizedSearchValue))) {
        acc.includesMatches.push(store)
      }
      return acc
    },
    {startsWithMatches: [], includesMatches: []}
  )
  
  const orderedFilteredStoreList = [...startsWithMatches, ...includesMatches]
  if (orderedFilteredStoreList?.length) {
    return orderedFilteredStoreList
  }


  return [];
}

export default StoreLocationModalTheme2;